import React, { useState, useRef, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { ChevronLeft, ChevronRight, Map, Target, Clock, Building2, CheckSquare, MessageCircle, Phone, PlusCircle, 
  BarChart3, Briefcase, DollarSign, Cpu, Megaphone, Users, Grid, FileText, Shield, UserCheck,
  Rocket, Zap, TrendingUp, Star, Circle, CheckCircle2, Lock, PlayCircle } from 'lucide-react';
import { FaBook } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import roadmapTemplateService from '../services/roadmapTemplateService';
import clientRoadmapService from '../services/clientRoadmapService';
import { getPillarEmoji } from '../utils/portalEmojis';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { createTask, uploadTaskFile } from '../services/taskService';
import { Link, useNavigate } from 'react-router-dom';
import Stepper, { Step } from '../components/Stepper';
import { createQuestion } from '../services/firebase/questions';
import { getPillarQuestionAssigneeUser, getPillarScheduleLink } from '../services/pillarsService';
import { X, ChevronDown, Upload } from 'lucide-react';
import { FaTimes, FaPaperclip } from 'react-icons/fa';
import { getAllInternalUsers } from '../services/internalUserService';
import { filterPortalAssignable } from '../utils/portalAssignable';
import { getInternalUsers } from '../services/userService';
import { FaLock } from 'react-icons/fa';

// Lock roadmap page - set to true to make it a teaser/preview only
const IS_ROADMAP_LOCKED = true;

// Static dummy roadmap data - always shown
// Items before ORG Chart (id: '11') are completed (green)
// ORG Chart is in-progress (yellow)
// Items after ORG Chart are locked
const STATIC_ROADMAP_DATA = [
  { id: '1', title: 'Establish Your Goals', emoji: '🎯', department: 'General', status: 'completed' },
  { id: '2', title: 'Take A Portal Tour', emoji: '🖱️', department: 'General', status: 'completed' },
  { id: '3', title: 'Explore Our In-Person Opportunities', emoji: '✈️', department: 'General', status: 'completed' },
  { id: '4', title: 'Tech Stack', emoji: '🌐', department: 'Technology', status: 'completed' },
  { id: '5', title: 'Standard Operating Procedures', emoji: '⚙️', department: 'Operations', status: 'completed' },
  { id: '6', title: 'Website', emoji: '💻', department: 'Marketing', status: 'completed' },
  { id: '7', title: 'ChatGPT', emoji: '🤖', department: 'Technology', status: 'completed' },
  { id: '8', title: 'Markup vs Margin', emoji: '💰', department: 'Finance', status: 'completed' },
  { id: '9', title: 'Close Ratio', emoji: '✅', department: 'Sales', status: 'completed' },
  { id: '10', title: 'Leads Needed', emoji: '📞', department: 'Marketing', status: 'completed' },
  { id: '11', title: 'ORG Chart', emoji: '👥', department: 'Operations', status: 'in-progress' },
  { id: '12', title: 'Mission, Vision & Values', emoji: '💡', department: 'HR', status: 'locked' },
  { id: '13', title: 'CRM', emoji: '🏫', department: 'Technology', status: 'locked' },
  { id: '14', title: 'Job Descriptions', emoji: '📝', department: 'Operations', status: 'locked' },
  { id: '15', title: 'Google My Business', emoji: '📊', department: 'Marketing', status: 'locked' },
  { id: '16', title: 'Website Plugins', emoji: '🔌', department: 'Technology', status: 'locked' },
  { id: '17', title: 'Labor Burden', emoji: '⚖️', department: 'Finance', status: 'locked' },
  { id: '18', title: 'Job Listings', emoji: '🛠️', department: 'HR', status: 'locked' },
  { id: '19', title: 'Automation', emoji: '⚡', department: 'Technology', status: 'locked' },
  { id: '20', title: 'Website Chatbot', emoji: '💬', department: 'Technology', status: 'locked' },
  { id: '21', title: 'ROI Data', emoji: '💱', department: 'Marketing', status: 'locked' },
  { id: '22', title: 'Social Media', emoji: '📱', department: 'Marketing', status: 'locked' },
  { id: '23', title: 'Paid Media', emoji: '💳', department: 'Marketing', status: 'locked' },
  { id: '24', title: 'Newsletter', emoji: 'ℹ️', department: 'Marketing', status: 'locked' },
  { id: '25', title: 'Hiring Materials', emoji: '📚', department: 'HR', status: 'locked' },
  { id: '26', title: 'Onboarding Materials', emoji: '📚', department: 'HR', status: 'locked' },
  { id: '27', title: 'Employee Handbook', emoji: '📖', department: 'HR', status: 'locked' },
  { id: '28', title: 'Hiring', emoji: '👤', department: 'HR', status: 'locked' },
  { id: '29', title: 'Partner Programs', emoji: '🚀', department: 'Sales', status: 'locked' },
  { id: '30', title: 'Proposals', emoji: '✏️', department: 'Sales', status: 'locked' },
  { id: '31', title: 'Key Performance Indicators', emoji: '🌟', department: 'Operations', status: 'locked' },
  { id: '32', title: 'Pardon Our Dust', emoji: '🔨', department: 'Sales', status: 'locked' },
  { id: '33', title: 'Awards', emoji: '🏆', department: 'Sales', status: 'locked' },
  { id: '34', title: 'Sales Scripts', emoji: '📋', department: 'Sales', status: 'locked' },
  { id: '35', title: 'Design Pricing', emoji: '💲', department: 'Sales', status: 'locked' },
  { id: '36', title: 'Project Runway', emoji: '🛣️', department: 'Finance', status: 'locked' },
  { id: '37', title: 'Overhead', emoji: '🚚', department: 'Finance', status: 'locked' },
  { id: '38', title: 'Margin', emoji: '🔐', department: 'Finance', status: 'locked' },
  { id: '39', title: 'Debt', emoji: '‼️', department: 'Finance', status: 'locked' },
  { id: '40', title: 'Forecasting', emoji: '🔍', department: 'Finance', status: 'locked' },
  { id: '41', title: 'Sales Flow', emoji: '💎', department: 'Sales', status: 'locked' },
  { id: '42', title: 'Shortcuts', emoji: '📲', department: 'Technology', status: 'locked' }
];

// Custom breathing animation CSS
const breathingAnimationStyle = `
  @keyframes breathing {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
      border-color: rgb(16, 185, 129);
    }
    50% {
      box-shadow: 0 0 0 20px rgba(16, 185, 129, 0);
      border-color: rgb(34, 197, 94);
    }
  }
  
  .breathing-green {
    animation: breathing 2s ease-in-out infinite;
  }
`;

const RoadmapItem = ({ item, isActive, index, totalItems, currentStep, onAskQuestion, onScheduleCall, onCreateTask, onTemplateComplete }) => {
  const { darkMode } = useTheme();
  const { isOnboardingMode, hasUsedOnboardingKey, markStepComplete } = useOnboarding();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isCompleted = index < currentStep;
  const isInProgress = index === currentStep;
  const isUpcoming = index > currentStep;
  
  // For onboarding mode, always show as in progress
  const showAsInProgress = isOnboardingMode && hasUsedOnboardingKey && item.department === 'onboarding';

  // Get storage key for this item's task step
  const getTaskStepKey = () => {
    if (!currentUser || !item.id) return null;
    return `roadmap_task_step_${currentUser.uid}_${item.id}`;
  };

  // Load saved task step from localStorage
  const getSavedTaskStep = () => {
    const key = getTaskStepKey();
    if (!key) return 1;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const step = parseInt(saved, 10);
        // Ensure step is within valid range
        if (item.tasks && item.tasks.length > 0) {
          return Math.max(1, Math.min(step, item.tasks.length));
        }
      }
    } catch (error) {
      console.error('Error loading saved task step:', error);
    }
    return 1;
  };

  // Save task step to localStorage
  const saveTaskStep = (step) => {
    const key = getTaskStepKey();
    if (!key) return;
    try {
      localStorage.setItem(key, step.toString());
    } catch (error) {
      console.error('Error saving task step:', error);
    }
  };

  // Get initial step from saved value or default to 1
  const [currentTaskStep, setCurrentTaskStep] = useState(() => getSavedTaskStep());

  // Reload saved step when item changes or becomes active
  useEffect(() => {
    if (isInProgress || showAsInProgress) {
      const key = getTaskStepKey();
      if (key) {
        try {
          const saved = localStorage.getItem(key);
          if (saved) {
            const step = parseInt(saved, 10);
            // Ensure step is within valid range
            if (item.tasks && item.tasks.length > 0) {
              const validStep = Math.max(1, Math.min(step, item.tasks.length));
              setCurrentTaskStep(validStep);
            }
          }
        } catch (error) {
          console.error('Error loading saved task step:', error);
        }
      }
    }
  }, [item.id, item.tasks, isInProgress, showAsInProgress, currentUser]);

  // Removed automatic task creation - tasks are no longer created from roadmap templates

  // Sample completion dates for completed steps
  const completionDates = {
    0: "January 15, 2024",
    1: "February 28, 2024"
  };

  // Color scheme for steps - cycling through colors
  const stepColors = [
    { bg: 'bg-red-500', border: 'border-red-500', text: 'text-red-600 dark:text-red-400', light: 'bg-red-50 dark:bg-red-900/20' },
    { bg: 'bg-yellow-500', border: 'border-yellow-500', text: 'text-yellow-600 dark:text-yellow-400', light: 'bg-yellow-50 dark:bg-yellow-900/20' },
    { bg: 'bg-teal-500', border: 'border-teal-500', text: 'text-teal-600 dark:text-teal-400', light: 'bg-teal-50 dark:bg-teal-900/20' },
    { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-600 dark:text-blue-400', light: 'bg-blue-50 dark:bg-blue-900/20' },
    { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-600 dark:text-green-400', light: 'bg-green-50 dark:bg-green-900/20' },
    { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-600 dark:text-purple-400', light: 'bg-purple-50 dark:bg-purple-900/20' },
    { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-600 dark:text-orange-400', light: 'bg-orange-50 dark:bg-orange-900/20' },
    { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-600 dark:text-pink-400', light: 'bg-pink-50 dark:bg-pink-900/20' }
  ];
  
  const colorScheme = stepColors[index % stepColors.length];
  
  // Icon mapping based on department or index
  const getStepIcon = (sizeClass) => {
    const iconMap = {
      'sales': <TrendingUp className={sizeClass} />,
      'operations': <Briefcase className={sizeClass} />,
      'finance': <DollarSign className={sizeClass} />,
      'technology': <Cpu className={sizeClass} />,
      'marketing': <Megaphone className={sizeClass} />,
      'onboarding': <Rocket className={sizeClass} />
    };
    
    return iconMap[item.department] || <Circle className={sizeClass} />;
  };

  const isCurrentStep = isInProgress || showAsInProgress;
  const nodeSize = isCurrentStep ? 'w-24 h-24' : isCompleted ? 'w-16 h-16' : 'w-14 h-14';
  const iconSize = isCurrentStep ? 'w-12 h-12' : 'w-7 h-7';

  // Get emoji based on department or status
  const getStepEmoji = () => {
    // Use emoji from template if available
    if (item && item.emoji) return item.emoji;
    
    if (isCompleted) return '✅';
    if (isInProgress || showAsInProgress) return '🚀';

    const dept = (item && item.department && typeof item.department === 'string')
      ? item.department.toLowerCase()
      : '';
    return getPillarEmoji(dept);
  };

  // Get department pill color
  const getDepartmentPillColor = (dept) => {
    const colorMap = {
      'sales': 'bg-red-500 text-white',
      'operations': 'bg-blue-500 text-white',
      'finance': 'bg-green-500 text-white',
      'technology': 'bg-purple-500 text-white',
      'marketing': 'bg-orange-500 text-white',
      'hr': 'bg-yellow-500 text-white',
      'onboarding': 'bg-gray-500 text-white'
    };
    return colorMap[dept] || 'bg-gray-500 text-white';
  };

  // Get department display name
  const getDepartmentName = (dept) => {
    const nameMap = {
      'sales': 'Sales',
      'operations': 'Operations',
      'finance': 'Finance',
      'technology': 'Technology',
      'marketing': 'Marketing',
      'hr': 'HR',
      'onboarding': 'Onboarding',
      'general': 'General'
    };
    // If not in map, capitalize first letter
    if (nameMap[dept?.toLowerCase()]) {
      return nameMap[dept.toLowerCase()];
    }
    // Capitalize first letter of each word
    return dept ? dept.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : 'General';
  };

  return (
    <div className={`relative ${isCurrentStep ? 'my-2' : 'my-0'}`}>
      {/* Step Container */}
      <div className={`relative flex ${isCurrentStep ? 'flex-col items-center gap-2' : 'items-center gap-6'}`}>
        {/* Content Box - Much larger for current step */}
        <div 
          className={`${isCurrentStep ? 'w-full max-w-4xl' : 'flex-1'} transition-all duration-300 ${
            !isCurrentStep && (isCompleted 
              ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg shadow-lg' 
              : 'bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-lg')
          }`}
        >
          {isCurrentStep ? (
            // Giant Stepper for current step with sidebar
            <div className="p-3 w-full flex gap-8">
              {/* Main content - 2/3 width */}
              <div className="w-2/3">
                {/* Header */}
                <div className="text-center mb-3">
                {item.department && (
                  <div className="mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDepartmentPillColor(item.department)}`}>
                      {getDepartmentName(item.department)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-2xl">{getStepEmoji()}</span>
                  <h3 className="font-bold text-3xl text-gray-900 dark:text-white">
                    {item.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">
                  {item.description}
                </p>
              </div>

              {/* Stepper with Tasks */}
              {(isCompleted || isInProgress || showAsInProgress) && item.tasks && item.tasks.length > 0 ? (
                <div className="w-full max-w-3xl mx-auto relative">
                  {IS_ROADMAP_LOCKED && (
                    <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                      <div className="text-center p-6">
                        <Lock className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Roadmap Preview</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">This is a preview. Full access coming soon!</p>
                      </div>
                    </div>
                  )}
                  <Stepper
                    initialStep={currentTaskStep}
                    onStepChange={(step) => {
                      if (IS_ROADMAP_LOCKED) return;
                      console.log(`Step ${step} of ${item.tasks.length}`);
                      setCurrentTaskStep(step);
                      saveTaskStep(step);
                    }}
                    onFinalStepCompleted={async () => {
                      if (IS_ROADMAP_LOCKED) return;
                      console.log("All tasks completed!");
                      // Save the final step (totalSteps + 1) or reset to last step
                      if (item.tasks && item.tasks.length > 0) {
                        const finalStep = item.tasks.length;
                        setCurrentTaskStep(finalStep);
                        saveTaskStep(finalStep);
                      }
                      // Call the completion handler to mark template as completed and move to next
                      if (onTemplateComplete) {
                        await onTemplateComplete(item, index);
                      }
                    }}
                    backButtonText="Previous"
                    nextButtonText="Next"
                    stepCircleContainerClassName="bg-transparent"
                    contentClassName="min-h-[200px]"
                    stepContainerClassName="p-4"
                    disableStepIndicators={IS_ROADMAP_LOCKED}
                    backButtonProps={IS_ROADMAP_LOCKED ? { disabled: true, className: 'opacity-50 cursor-not-allowed' } : {}}
                    nextButtonProps={IS_ROADMAP_LOCKED ? { disabled: true, className: 'opacity-50 cursor-not-allowed' } : {}}
                  >
                    {item.tasks.map((task, taskIndex) => {
                      let taskLink = null;
                      if (task.title === 'Sign your NDA') {
                        taskLink = '/start/nda';
                      } else if (task.title === 'Complete your Intake') {
                        taskLink = '/start/existing-conditions';
                      } else if (task.title === 'Create your Roadmap') {
                        taskLink = '/start/proposal';
                      }

                      return (
                        <Step key={taskIndex}>
                          <div className="py-3">
                            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                              {task.title}
                            </h2>
                            {task.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
                                {task.description}
                              </p>
                            )}
                            {/* View Course or View Page buttons */}
                            {(task.courseId || task.courseTitle || task.portalPage) && (
                              <div className="mt-3 space-y-2">
                                {task.courseId && (
                                  <div
                                    className={`inline-flex items-center px-4 py-2 text-white font-medium rounded-lg transition-colors text-sm ${
                                      IS_ROADMAP_LOCKED 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-red-500 hover:bg-red-600 cursor-pointer'
                                    }`}
                                    onClick={(e) => IS_ROADMAP_LOCKED && e.preventDefault()}
                                  >
                                    View Course
                                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                  </div>
                                )}
                                {task.courseTitle && !task.courseId && (
                                  <div
                                    className={`inline-flex items-center px-4 py-2 text-white font-medium rounded-lg transition-colors text-sm ${
                                      IS_ROADMAP_LOCKED 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-red-500 hover:bg-red-600 cursor-pointer'
                                    }`}
                                    onClick={(e) => IS_ROADMAP_LOCKED && e.preventDefault()}
                                  >
                                    View Course
                                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                  </div>
                                )}
                                {task.portalPage && (
                                  <div
                                    className={`inline-flex items-center px-4 py-2 text-white font-medium rounded-lg transition-colors text-sm ${
                                      IS_ROADMAP_LOCKED 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-red-500 hover:bg-red-600 cursor-pointer'
                                    }`}
                                    onClick={(e) => IS_ROADMAP_LOCKED && e.preventDefault()}
                                  >
                                    View Page
                                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            )}
                            {taskLink && (
                              <div className="mt-4">
                                <div
                                  className={`inline-flex items-center px-4 py-2 text-white font-medium rounded-lg transition-colors text-sm ${
                                    IS_ROADMAP_LOCKED 
                                      ? 'bg-gray-400 cursor-not-allowed' 
                                      : 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                                  }`}
                                  onClick={(e) => IS_ROADMAP_LOCKED && e.preventDefault()}
                                >
                                  Go to {task.title}
                                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </Step>
                      );
                    })}
                  </Stepper>
                </div>
                ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400 text-base">No tasks available for this step.</p>
                </div>
              )}
              </div>

              {/* Sidebar - 1/3 width */}
              <div className="w-1/3 flex flex-col gap-4 h-full relative">
                <button 
                  onClick={() => !IS_ROADMAP_LOCKED && onAskQuestion && onAskQuestion(item.department)}
                  disabled={IS_ROADMAP_LOCKED}
                  className={`flex-1 p-6 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors flex flex-col items-center justify-center text-center ${
                    IS_ROADMAP_LOCKED 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <MessageCircle className={`w-12 h-12 mb-3 ${IS_ROADMAP_LOCKED ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`} />
                  <div className="font-semibold text-gray-800 dark:text-white mb-2 text-lg">Ask a Question</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Get help with this step</div>
                </button>
                <button 
                  onClick={() => !IS_ROADMAP_LOCKED && onScheduleCall && onScheduleCall(item.department)}
                  disabled={IS_ROADMAP_LOCKED}
                  className={`flex-1 p-6 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors flex flex-col items-center justify-center text-center ${
                    IS_ROADMAP_LOCKED 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Phone className={`w-12 h-12 mb-3 ${IS_ROADMAP_LOCKED ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`} />
                  <div className="font-semibold text-gray-800 dark:text-white mb-2 text-lg">Schedule a Call</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Book time with your advisor</div>
                </button>
                <button 
                  onClick={() => !IS_ROADMAP_LOCKED && onCreateTask && onCreateTask(item.department)}
                  disabled={IS_ROADMAP_LOCKED}
                  className={`flex-1 p-6 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors flex flex-col items-center justify-center text-center ${
                    IS_ROADMAP_LOCKED 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <PlusCircle className={`w-12 h-12 mb-3 ${IS_ROADMAP_LOCKED ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}`} />
                  <div className="font-semibold text-gray-800 dark:text-white mb-2 text-lg">Create a Task</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Add a custom task</div>
                </button>
              </div>
            </div>
          ) : (
            // Compact view for non-current steps
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className={`font-bold flex items-center gap-2 ${
                    isCompleted
                      ? 'text-green-600 dark:text-green-400 text-xl'
                      : 'text-gray-700 dark:text-gray-300 text-lg'
                  }`}>
                    <span>{getStepEmoji()}</span>
                    <span>{item.title}</span>
                  </h3>
                </div>
                {isCompleted && (
                  <div className="ml-4">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Question Modal Component - from MyQuestions.js
const QuestionModal = ({ isOpen, onClose, department }) => {
  const [questionText, setQuestionText] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  // Load internal users when modal opens
  useEffect(() => {
    if (isOpen && internalUsers.length === 0) {
      const loadUsers = async () => {
        try {
          setModalLoading(true);
          let users = [];
          try {
            users = await getAllInternalUsers();
          } catch (error) {
            try {
              users = await getInternalUsers();
            } catch (fallbackError) {
              console.error('Error loading users:', fallbackError);
              users = [];
            }
          }
          setInternalUsers(users);
          setModalLoading(false);
        } catch (error) {
          console.error('Error loading users:', error);
          setInternalUsers([]);
          setModalLoading(false);
        }
      };
      loadUsers();
    }
  }, [isOpen, internalUsers.length]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size <= 10 * 1024 * 1024) {
        setFile(selectedFile);
        setFileError('');
      } else {
        setFileError('File must be less than 10MB');
        setFile(null);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setFileError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) {
      alert('Please enter your question.');
      return;
    }
    if (!assigneeId) {
      alert('Please select someone to ask.');
      return;
    }
    setIsSubmitting(true);
    try {
      const selectedUser = internalUsers.find(user => user.id === assigneeId);
      const questionData = {
        accountId: currentUser.uid,
        accountName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        assigneeId: assigneeId === 'anyone' ? 'anyone' : assigneeId,
        assigneeName: assigneeId === 'anyone' ? 'Anyone' : (selectedUser?.name || `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim()),
        questionText: questionText.trim(),
        department: department,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User'
      };
      await createQuestion(questionData);
      setQuestionText('');
      setAssigneeId('');
      setFile(null);
      setFileError('');
      onClose();
    } catch (error) {
      console.error('Error submitting question:', error);
      alert('Failed to submit question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    onClose();
    setQuestionText('');
    setAssigneeId('');
    setFile(null);
    setFileError('');
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1"></div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Ask a Question</h2>
          <div className="flex-1 flex justify-end">
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-6">
          {modalLoading ? (
            <div className="flex items-center justify-center py-8">
              <BBALoader size={32} />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Question *
                </label>
                <textarea
                  id="questionText"
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  rows="6"
                  placeholder="Type your question here..."
                  required
                />
              </div>
              <div>
                <label htmlFor="assigneeId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ask *
                </label>
                <select
                  id="assigneeId"
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="" disabled>Select someone to ask...</option>
                  <option value="anyone">Anyone</option>
                  {filterPortalAssignable(internalUsers).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attach File (Optional)
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200">
                    <FaPaperclip className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                    />
                  </label>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Click to upload a file (max 10MB)
                    </p>
                    {fileError && (
                      <p className="text-red-500 text-sm mt-1">{fileError}</p>
                    )}
                  </div>
                </div>
                {file && (
                  <div className="mt-3 flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FaPaperclip className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <FaTimes className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200 ${
                    isSubmitting
                      ? 'bg-red-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Question'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// UserAvatar component for CreateTaskModal
const UserAvatar = ({ user, size = 'md', className = '' }) => {
  const { darkMode } = useTheme();
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };
  const sizeClass = sizeClasses[size] || sizeClasses.md;

  if (user.profileImage) {
    return (
      <img
        src={user.profileImage}
        alt={user.name}
        className={`${sizeClass} rounded-full object-cover ${className}`}
      />
    );
  }

  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center font-semibold ${className} ${
      darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600'
    }`}>
      {user.initials}
    </div>
  );
};

// Create Task Modal Component - from PortalTasks.js
const CreateTaskModal = ({ isOpen, onClose, department }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [newTask, setNewTask] = useState({
    title: '',
    assignedTo: [],
    description: '',
    files: []
  });
  const [internalUsers, setInternalUsers] = useState([]);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [error, setError] = useState(null);
  const assigneeDropdownRef = useRef(null);

  // Load internal users when modal opens
  useEffect(() => {
    if (isOpen && internalUsers.length === 0) {
      const loadUsers = async () => {
        try {
          const users = await getInternalUsers();
          setInternalUsers(users);
        } catch (error) {
          console.error('Error loading users:', error);
          setInternalUsers([]);
        }
      };
      loadUsers();
    }
  }, [isOpen, internalUsers.length]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setShowAssigneeDropdown(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleCreateTask = async () => {
    if (!currentUser) return;
    
    try {
      if (!newTask.title.trim()) {
        setError('Task title is required');
        return;
      }

      let clientAccountId = 'portal';
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accountId) {
            clientAccountId = userData.accountId;
          }
        }
      } catch (error) {
        console.error('Error getting client account ID:', error);
      }

      const taskData = {
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        assignees: newTask.assignedTo.filter(id => id),
        accountId: clientAccountId,
        department: department,
        completed: false,
        status: 'Not Done',
        files: [],
        createdBy: currentUser.uid
      };

      const createdTask = await createTask(taskData);
      
      // Upload files if any
      if (newTask.files && newTask.files.length > 0) {
        for (const file of newTask.files) {
          try {
            await uploadTaskFile(createdTask.id, file);
          } catch (fileError) {
            console.error('Error uploading file:', fileError);
          }
        }
      }

      setNewTask({ title: '', assignedTo: [], description: '', files: [] });
      setError(null);
      onClose();
      alert('Task created successfully!');
    } catch (error) {
      console.error('Error creating task:', error);
      setError('Failed to create task. Please try again.');
    }
  };

  const closeModal = () => {
    setNewTask({ title: '', assignedTo: [], description: '', files: [] });
    setError(null);
    setShowAssigneeDropdown(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg w-full max-w-2xl`}>
        <div className={`flex justify-between items-center p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="w-6"></div>
          <h2 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>New Task</h2>
          <button 
            className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
            onClick={closeModal}
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6">
          {error && <p className={`text-red-500 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{error}</p>}
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Task Title *
            </label>
            <input
              type="text"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
              placeholder="Enter task title"
            />
          </div>
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Description
            </label>
            <textarea
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
              placeholder="Enter task description"
            />
          </div>
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Assigned To
            </label>
            <div className="relative" ref={assigneeDropdownRef}>
              <button
                type="button"
                onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                className={`w-full flex items-center justify-between px-3 py-2 border rounded-md focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
              >
                <div className="flex items-center gap-2">
                  {newTask.assignedTo.length > 0 ? (
                    newTask.assignedTo.map(userId => {
                      const user = internalUsers.find(u => u.id === userId);
                      return user ? (
                        <UserAvatar 
                          key={userId}
                          user={{
                            name: user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}`
                              : user.firstName 
                              ? user.firstName
                              : user.email?.split('@')[0] || 'Unknown',
                            initials: user.firstName && user.lastName
                              ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
                              : user.firstName
                              ? user.firstName.substring(0, 2).toUpperCase()
                              : (user.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase(),
                            profileImage: user.profileImage
                          }} 
                          size="sm" 
                        />
                      ) : null;
                    })
                  ) : (
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Select assignees...
                    </span>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${showAssigneeDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showAssigneeDropdown && (
                <div className={`absolute z-10 w-full mt-1 border rounded-md shadow-lg ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                  <div className="max-h-60 overflow-y-auto">
                    {filterPortalAssignable(internalUsers).map(user => (
                      <label
                        key={user.id}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 ${darkMode ? 'hover:bg-gray-600' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={newTask.assignedTo.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewTask(prev => ({
                                ...prev,
                                assignedTo: [...prev.assignedTo, user.id]
                              }));
                            } else {
                              setNewTask(prev => ({
                                ...prev,
                                assignedTo: prev.assignedTo.filter(id => id !== user.id)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                        />
                        <UserAvatar 
                          user={{
                            name: user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}`
                              : user.firstName 
                              ? user.firstName
                              : user.email?.split('@')[0] || 'Unknown',
                            initials: user.firstName && user.lastName
                              ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
                              : user.firstName
                              ? user.firstName.substring(0, 2).toUpperCase()
                              : (user.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase(),
                            profileImage: user.profileImage
                          }} 
                          size="sm" 
                        />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}`
                            : user.firstName 
                            ? user.firstName
                            : user.email?.split('@')[0] || 'Unknown'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Attachments
            </label>
            <div className="space-y-3">
              <label className={`flex items-center gap-2 cursor-pointer w-full px-3 py-2 border rounded-md transition-colors ${darkMode 
                ? 'text-gray-400 hover:text-gray-300 bg-gray-700 border-gray-600 hover:border-gray-500' 
                : 'text-gray-600 hover:text-gray-800 bg-white border-gray-300 hover:border-gray-400'
              }`}>
                <Upload size={18} />
                <span>Choose files to attach</span>
                <input 
                  type="file" 
                  multiple 
                  className="hidden" 
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files);
                    setNewTask(prev => ({
                      ...prev,
                      files: [...prev.files, ...newFiles]
                    }));
                  }}
                />
              </label>
              {newTask.files && newTask.files.length > 0 && (
                <div className="space-y-2">
                  <div className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Selected files ({newTask.files.length}):
                  </div>
                  {newTask.files.map((file, index) => (
                    <div key={index} className={`flex items-center justify-between p-2 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2">
                        <Upload size={14} className="text-gray-500" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button 
                        onClick={() => setNewTask(prev => ({
                          ...prev,
                          files: prev.files.filter((_, i) => i !== index)
                        }))}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={`p-4 border-t flex justify-end gap-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button 
            className={`px-4 py-2 rounded-md border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            onClick={closeModal}
          >
            Cancel
          </button>
          <button 
            className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
            onClick={handleCreateTask}
          >
            Create task
          </button>
        </div>
      </div>
    </div>
  );
};

// Get status-based border and background colors
const getStatusColors = (status) => {
  switch (status) {
    case 'completed':
      return 'border-green-500 bg-green-50 dark:bg-green-900/20';
    case 'in-progress':
      return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
    case 'locked':
    default:
      return 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800';
  }
};

// Department Card View Component - Static Preview Style
const DepartmentCardView = ({ roadmapItems, loading, activeIndex }) => {
  const { darkMode } = useTheme();
  const { isMobile, isTablet } = useResponsive();

  // Get department pill color based on department
  const getDepartmentPillColor = (dept) => {
    const deptLower = dept?.toLowerCase() || '';
    const colorMap = {
      'sales': 'bg-red-500 text-white',
      'operations': 'bg-blue-500 text-white',
      'finance': 'bg-green-500 text-white',
      'technology': 'bg-purple-500 text-white',
      'marketing': 'bg-orange-500 text-white',
      'hr': 'bg-yellow-500 text-white',
      'onboarding': 'bg-gray-500 text-white',
      'general': 'bg-gray-500 text-white'
    };
    return colorMap[deptLower] || 'bg-gray-500 text-white';
  };

  // Get department name
  const getDepartmentName = (dept) => {
    const deptLower = dept?.toLowerCase() || '';
    const nameMap = {
      'sales': 'Sales',
      'operations': 'Operations',
      'finance': 'Finance',
      'technology': 'Technology',
      'marketing': 'Marketing',
      'hr': 'HR',
      'onboarding': 'Onboarding',
      'general': 'General'
    };
    return nameMap[deptLower] || dept || 'General';
  };

  return (
    <div className={isMobile ? 'p-2' : isTablet ? 'p-3' : 'p-4'}>
      <div className={`grid ${isMobile ? 'grid-cols-1' : isTablet ? 'grid-cols-2' : 'grid-cols-3'} ${isMobile ? 'gap-2' : 'gap-4'}`}>
        {roadmapItems.map((item, index) => (
          <div
            key={item.id}
            className={`p-6 rounded-lg shadow-md border-2 relative ${getStatusColors(item.status || 'locked')}`}
          >
            {/* Lock icon */}
            <div className="absolute top-3 right-3">
              <FaLock className="text-gray-400 dark:text-gray-500 text-sm" />
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="text-4xl mb-3">
                {item.emoji || '📋'}
              </div>
              <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                {item.title}
              </h3>
              <div className="mt-auto">
                <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getDepartmentPillColor(item.department)}`}>
                  {getDepartmentName(item.department)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StepView = ({ activeIndex, setActiveIndex, roadmapItems, setRoadmapItems, allRoadmapItems, setAllRoadmapItems, roadmapRef, itemsRef }) => {
  const { darkMode } = useTheme();
  const { isOnboardingMode, hasUsedOnboardingKey, markStepComplete } = useOnboarding();
  const { currentUser } = useAuth();
  
  // Find in-progress item index to initialize currentStep
  const getInProgressIndex = () => {
    if (!roadmapItems || roadmapItems.length === 0) {
      return isOnboardingMode && hasUsedOnboardingKey ? 0 : 0;
    }
    const inProgressIndex = roadmapItems.findIndex(item => 
      item.status === 'in-progress' || item.status === 'in_progress' || item.status === 'active'
    );
    return inProgressIndex !== -1 ? inProgressIndex : 0;
  };
  
  const [currentStep, setCurrentStep] = useState(() => getInProgressIndex());
  
  // Update currentStep when roadmapItems change to show in-progress item
  useEffect(() => {
    if (roadmapItems && roadmapItems.length > 0) {
      const inProgressIndex = roadmapItems.findIndex(item => 
        item.status === 'in-progress' || item.status === 'in_progress' || item.status === 'active'
      );
      if (inProgressIndex !== -1) {
        setCurrentStep(inProgressIndex);
        // Also update activeIndex to match
        if (activeIndex !== inProgressIndex) {
          setActiveIndex(inProgressIndex);
        }
      } else if (activeIndex !== undefined && activeIndex !== null && activeIndex < roadmapItems.length) {
        // If no in-progress item, use activeIndex if valid
        setCurrentStep(activeIndex);
      }
    }
  }, [roadmapItems, activeIndex, setActiveIndex]);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);

  const scrollToItem = (index) => {
    if (index < 0 || index >= roadmapItems.length) return;
    
    const itemRef = itemsRef.current[index];
    if (itemRef && roadmapRef.current) {
      const containerRect = roadmapRef.current.getBoundingClientRect();
      const itemRect = itemRef.getBoundingClientRect();
      
      roadmapRef.current.scrollTo({
        top: itemRef.offsetTop - containerRect.height / 2 + itemRect.height / 2,
        behavior: 'smooth'
      });
      
      setActiveIndex(index);
    }
  };

  // Center the current step on mount and when activeIndex or roadmapItems changes
  useEffect(() => {
    scrollToItem(activeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, roadmapItems]);
  
  // Handlers for modals
  const handleAskQuestion = (department) => {
    if (IS_ROADMAP_LOCKED) return;
    setCurrentDepartment(department);
    setShowQuestionModal(true);
  };

  // Scheduling is a members-only action; the roadmap's Schedule Call tile is a
  // locked teaser here, so there is nothing to open.
  const handleScheduleCall = () => {};

  const handleCreateTask = (department) => {
    if (IS_ROADMAP_LOCKED) return;
    setCurrentDepartment(department);
    setShowCreateTaskModal(true);
  };

  // Handle template completion - mark current as completed and next as in-progress
  const handleTemplateComplete = async (completedItem, completedIndex) => {
    if (IS_ROADMAP_LOCKED || !currentUser) return;

    try {
      // Get user's accountId
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        console.error('User document not found');
        return;
      }

      const userData = userDoc.data();
      const accountId = userData.accountId;

      if (!accountId) {
        console.error('No accountId found for user');
        return;
      }

      // Update current template to completed
      const templateId = completedItem.clientRoadmapId || completedItem.id;
      await clientRoadmapService.updateTemplateInClientRoadmap(accountId, templateId, {
        status: 'completed',
        dateCompleted: new Date().toISOString()
      });

      // Find next template and update to in-progress
      const nextIndex = completedIndex + 1;
      if (nextIndex < roadmapItems.length) {
        const nextItem = roadmapItems[nextIndex];
        const nextTemplateId = nextItem.clientRoadmapId || nextItem.id;
        await clientRoadmapService.updateTemplateInClientRoadmap(accountId, nextTemplateId, {
          status: 'in-progress',
          dateStarted: new Date().toISOString()
        });

        // Reload roadmap data from Firestore to ensure we have the latest status
        const updatedRoadmap = await clientRoadmapService.getClientRoadmap(accountId);
        const updatedTransformedItems = updatedRoadmap.map((template) => {
          let status = 'locked';
          if (template.status === 'completed' || template.status === 'done') {
            status = 'completed';
          } else if (template.status === 'in-progress' || template.status === 'in_progress' || template.status === 'active') {
            status = 'in-progress';
          } else if (template.status === 'not-started' || template.status === 'not_started' || template.status === 'locked') {
            status = 'locked';
          }

          return {
            id: template.clientRoadmapId || template.id || `template-${Date.now()}-${Math.random()}`,
            title: template.title || 'Untitled',
            description: template.description || '',
            goal: template.goal || '',
            department: (template.department || 'general').toLowerCase(),
            status: status,
            tasks: template.tasks || [],
            navigators: template.navigators || [],
            emoji: template.emoji || null,
            order: template.order,
            allOrder: template.allOrder
          };
        });

        updatedTransformedItems.sort((a, b) => {
          const orderA = a.order || a.allOrder || 0;
          const orderB = b.order || b.allOrder || 0;
          return orderA - orderB;
        });

        // Find the new in-progress item index
        const newInProgressIndex = updatedTransformedItems.findIndex(item => item.status === 'in-progress');
        
        // Update both cached and displayed items
        setAllRoadmapItems(updatedTransformedItems);
        setRoadmapItems(updatedTransformedItems);

        // Move to next item (the one that's now in-progress)
        if (newInProgressIndex !== -1) {
          setActiveIndex(newInProgressIndex);
          setCurrentStep(newInProgressIndex);
        } else if (nextIndex < updatedTransformedItems.length) {
          setActiveIndex(nextIndex);
          setCurrentStep(nextIndex);
        }
      } else {
        // No next item, just update current
        const updatedItems = [...roadmapItems];
        updatedItems[completedIndex] = { ...completedItem, status: 'completed' };
        setAllRoadmapItems(updatedItems);
        setRoadmapItems(updatedItems);
      }
    } catch (error) {
      console.error('Error completing template:', error);
    }
  };


  const reversedItems = [...roadmapItems].reverse();
  
  // Color scheme for path segments
  const stepColors = [
    { path: 'bg-red-500', completed: 'bg-green-500' },
    { path: 'bg-yellow-500', completed: 'bg-green-500' },
    { path: 'bg-teal-500', completed: 'bg-green-500' },
    { path: 'bg-blue-500', completed: 'bg-green-500' },
    { path: 'bg-green-500', completed: 'bg-green-500' },
    { path: 'bg-purple-500', completed: 'bg-green-500' },
    { path: 'bg-orange-500', completed: 'bg-green-500' },
    { path: 'bg-pink-500', completed: 'bg-green-500' }
  ];

  return (
    <div className={`flex ${isOnboardingMode && hasUsedOnboardingKey ? 'h-auto' : 'h-auto'} w-full mx-auto relative`}>
      <div className="flex flex-row flex-grow bg-transparent rounded-lg items-center">
        <div 
          ref={roadmapRef} 
          className={`flex-grow overflow-y-auto px-4 py-2 scrollbar-hide relative ${
            isOnboardingMode && hasUsedOnboardingKey ? 'flex items-center justify-center' : ''
          }`}
          style={{ scrollbarWidth: 'none' }}
        >
          <div className={`${isOnboardingMode && hasUsedOnboardingKey ? 'w-full max-w-4xl' : 'max-w-4xl'} mx-auto relative`}>
            {roadmapItems
              .filter((item, index) => index === currentStep)
              .map((item, index) => {
                const originalIndex = currentStep;
                return (
                  <div 
                    key={originalIndex} 
                    ref={el => itemsRef.current[originalIndex] = el}
                    className="relative"
                  >
                    <RoadmapItem 
                      item={item}
                      isActive={originalIndex === activeIndex} 
                      index={originalIndex}
                      totalItems={roadmapItems.length}
                      currentStep={currentStep}
                      onAskQuestion={handleAskQuestion}
                      onScheduleCall={handleScheduleCall}
                      onCreateTask={handleCreateTask}
                      onTemplateComplete={handleTemplateComplete}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Modals */}
      <QuestionModal
        isOpen={showQuestionModal}
        onClose={() => setShowQuestionModal(false)}
        department={currentDepartment}
      />
      <CreateTaskModal
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        department={currentDepartment}
      />
    </div>
  );
};

// Current Focus View Component - Shows ORG Chart as active (locked preview)
const CurrentFocusView = ({ roadmapItems }) => {
  const { darkMode } = useTheme();
  
  // Find ORG Chart item (id: '11')
  const orgChartIndex = roadmapItems.findIndex(item => item.id === '11');
  
  // Prepare ORG Chart item with default tasks/description for display
  const orgChartItem = roadmapItems.find(item => item.id === '11');
  const displayItem = orgChartItem ? {
    ...orgChartItem,
    description: orgChartItem.description || 'Build and organize your company structure with a clear organizational chart.',
    tasks: orgChartItem.tasks || [
      {
        id: 'task-1',
        title: 'Define Organizational Structure',
        description: 'Outline your company hierarchy and reporting structure',
        estimatedHours: 0,
        estimatedMinutes: 30,
        completed: false
      },
      {
        id: 'task-2',
        title: 'Map Roles and Responsibilities',
        description: 'Document key roles and their responsibilities within the organization',
        estimatedHours: 1,
        estimatedMinutes: 0,
        completed: false
      }
    ]
  } : null;

  // Empty handlers since it's locked
  const handleAskQuestion = () => {};
  const handleScheduleCall = () => {};
  const handleCreateTask = () => {};
  const handleTemplateComplete = () => {};

  if (!displayItem) {
    return <div className="p-4 text-center">No active focus item found.</div>;
  }

  return (
    <div className="flex h-auto w-full mx-auto relative">
      <div className="flex flex-row flex-grow bg-transparent rounded-lg items-center">
        <div className="flex-grow overflow-y-auto px-4 py-2 scrollbar-hide relative">
          <div className="max-w-4xl mx-auto relative">
            <RoadmapItem 
              item={displayItem}
              isActive={true} 
              index={orgChartIndex}
              totalItems={roadmapItems.length}
              currentStep={orgChartIndex}
              onAskQuestion={handleAskQuestion}
              onScheduleCall={handleScheduleCall}
              onCreateTask={handleCreateTask}
              onTemplateComplete={handleTemplateComplete}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StartRoadmap = () => {
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [loadingRoadmap, setLoadingRoadmap] = useState(true);
  const [department, setDepartment] = useState('all');

  // Overview data - combined items from all departments
  const overviewData = [
    { id: 'overview-1', title: 'CRM Implementation', description: 'Set up and configure customer relationship management system', status: 'completed' },
    { id: 'overview-2', title: 'Sales Process Optimization', description: 'Streamline sales workflows and improve conversion rates', status: 'in-progress' },
    { id: 'overview-3', title: 'Process Documentation', description: 'Document all key operational processes and procedures', status: 'completed' },
    { id: 'overview-4', title: 'Workflow Automation', description: 'Automate repetitive operational tasks and workflows', status: 'in-progress' },
    { id: 'overview-5', title: 'Financial Systems Setup', description: 'Configure accounting and financial management software', status: 'completed' },
    { id: 'overview-6', title: 'Budget Planning', description: 'Develop comprehensive budget planning and forecasting', status: 'in-progress' },
    { id: 'overview-7', title: 'Infrastructure Setup', description: 'Set up cloud infrastructure and hosting environment', status: 'completed' },
    { id: 'overview-8', title: 'Security Implementation', description: 'Implement security measures and protocols', status: 'in-progress' },
    { id: 'overview-9', title: 'Brand Identity', description: 'Develop and establish brand identity and guidelines', status: 'completed' },
    { id: 'overview-10', title: 'Content Strategy', description: 'Create comprehensive content marketing strategy', status: 'in-progress' },
    { id: 'overview-11', title: 'HRIS Implementation', description: 'Set up Human Resources Information System', status: 'completed' },
    { id: 'overview-12', title: 'Recruitment Process', description: 'Streamline recruitment and hiring processes', status: 'in-progress' },
    { id: 'overview-13', title: 'Lead Generation System', description: 'Implement automated lead generation and nurturing', status: 'locked' },
    { id: 'overview-14', title: 'Supply Chain Optimization', description: 'Improve supply chain efficiency and reduce costs', status: 'locked' },
    { id: 'overview-15', title: 'Financial Reporting', description: 'Create automated financial reporting dashboards', status: 'locked' },
    { id: 'overview-16', title: 'API Integration', description: 'Integrate third-party APIs and services', status: 'locked' },
    { id: 'overview-17', title: 'Social Media Campaign', description: 'Launch and manage social media campaigns', status: 'locked' },
    { id: 'overview-18', title: 'Employee Onboarding', description: 'Develop comprehensive employee onboarding program', status: 'locked' }
  ];

  // Onboarding roadmap items - simplified for onboarding mode
  const onboardingRoadmapItems = [
    {
      id: 'onboarding-1',
      title: 'Onboarding',
      description: 'Complete your onboarding process to unlock full access to the portal and begin your journey with Builders Breakthrough Advisors.',
      goal: 'Successfully complete all onboarding requirements',
      department: 'onboarding',
      tasks: [
        {
          id: 'task-1',
          title: 'Sign your NDA',
          description: 'Review and sign the Non-Disclosure Agreement to protect sensitive information',
          estimatedHours: 0,
          estimatedMinutes: 5,
          completed: false
        },
        {
          id: 'task-2',
          title: 'Complete your Intake',
          description: 'Fill out your business profile and share your current challenges and goals',
          estimatedHours: 0,
          estimatedMinutes: 60,
          completed: false
        },
        {
          id: 'task-3',
          title: 'Create your Roadmap',
          description: 'Work with our team to create your personalized business roadmap',
          estimatedHours: 1,
          estimatedMinutes: 0,
          completed: false
        }
      ],
      navigators: [
        { id: 1, name: 'Onboarding Specialist', role: 'Guide' }
      ]
    }
  ];

  // Always use static roadmap data - preview/teaser mode
  useEffect(() => {
    setLoadingRoadmap(false);
    // Always use static data
    setRoadmapItems(STATIC_ROADMAP_DATA);
  }, []);
  
  return (
    <div className="p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-transparent rounded-lg p-2">
          {/* Centered Title */}
          <div className="mb-6">
            <div className="text-center py-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Roadmap</h1>
            </div>
          </div>

          {/* Tabs — pill style, matching the portal Roadmap */}
          <div className="flex items-center justify-center gap-2 mb-4" aria-label="Tabs">
            <button
              onClick={() => setDepartment('all')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                department === 'all'
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Target className="w-3.5 h-3.5" />
              Current Focus
            </button>
            <button
              onClick={() => setDepartment('overview')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                department === 'overview'
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Grid className="w-3.5 h-3.5" />
              Overview
            </button>
          </div>

          {/* Content based on selected tab */}
          <div className="bg-transparent rounded-lg shadow-lg p-0">
            {department === 'all' ? (
              <CurrentFocusView roadmapItems={roadmapItems} />
            ) : (
              <DepartmentCardView roadmapItems={roadmapItems} loading={loadingRoadmap} activeIndex={null} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartRoadmap;

